import React, { useEffect, useState } from "react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Calendar } from "@/components/ui/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Switch } from "@/components/ui/switch"
import { Label } from "@/components/ui/label"
import { CalendarIcon, Loader2, User, Clock } from "lucide-react"
import { cn } from "@/lib/utils"
import { format } from "date-fns"
import { useToast } from "@/hooks/use-toast"
import { CheckSquare, DollarSign, X, ChevronDown, Check } from "lucide-react"
import Checklist from "@/components/ui/Checklist"
import { Badge } from "@/components/ui/badge"
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command"
import AssignmentSelector from "@/components/workflow-builder/nodes/AssignmentSelector"

// Update the function signature to include parentTasks:
export function TaskFormModal({
  open,
  onOpenChange,
  initialData,
  onSubmit,
  isSubmitting = false,
  parentTasks = [],
  availableWorkflows = [],
  categories = [],
  users = [],
  canAssignTasks = false,
  userRole = 'user',
  userDepartmentId = null,
  departments = [],
}) {
  const isEditing = !!initialData?.id
  const [errors, setErrors] = useState({})
  const { toast } = useToast()
  const [initialFormData, setInitialFormData] = useState(null) // Track initial form data for dirty checking
  const [isFormInitialized, setIsFormInitialized] = useState(false) // Track if form has been initialized
  // Update the initial formData state to include parent task fields:
  const [formData, setFormData] = useState({
    name: initialData?.name || "",
    description: initialData?.description || "",
    category: initialData?.category || "General",
    priority: initialData?.priority || "5",
    due: initialData?.due || "~today~",
    status: initialData?.status || "To Do",
    deferrable: initialData?.deferrable || 0,
    missable: initialData?.missable || 0,
    isParent: initialData?.isParent !== undefined ? initialData.isParent : true,
    parentId: initialData?.parent_id || null,
    parentName: initialData?.parentName || null,
    workflowId: initialData?.workflowId || null,
    workflowName: initialData?.workflowName || null,
    taskType: initialData?.taskType || "standard",
    taskTypeCategories: initialData?.taskTypeCategories || [],
    salesProbability: initialData?.salesProbability || 0,
    checklist: initialData?.checklist || [],
    link: initialData?.link || "",
    linkTitle: initialData?.linkTitle || "",
    assignedUserIds: initialData?.assignedUserIds || [],
    assignment: initialData?.assignment || { type: 'individual', user_ids: [], department_ids: [], shared: true },
    selectedTrigger: initialData?.selectedTrigger || null,
    triggerConfig: initialData?.triggerConfig || null,
    createTaskNow: initialData?.createTaskNow !== undefined ? initialData.createTaskNow : true,
  })

  // Reset form when modal opens/closes or initialData changes
  useEffect(() => {
    if (open && initialData && !isFormInitialized) {
      const newFormData = {
        ...initialData,
        parentId: initialData.parent_id || initialData.parentId || null,
        isParent: initialData.isParent !== undefined ? initialData.isParent : true,
      }
      setFormData(newFormData)
      setInitialFormData({...newFormData}) // Create a copy for dirty checking
      setIsFormInitialized(true)
      setErrors({})
    }
    // Update the reset form logic in the useEffect to include parent task fields:
    else if (!open) {
      setIsFormInitialized(false) // Reset the initialization flag
      // Small delay to avoid flickering during close animation
      const timeout = setTimeout(() => {
        if (!isEditing) {
          const defaultFormData = {
            name: "",
            description: "",
            category: "General",
            priority: "5",
            due: "~today~",
            status: "To Do",
            deferrable: 0,
            missable: 0,
            isParent: true, // Default to parent task for new tasks
            parentId: null,
            parentName: null,
            workflowId: null,
            workflowName: null,
            taskType: "standard",
            taskTypeCategories: [],
            salesProbability: 1,
            checklist: [],
            link: "",
            linkTitle: "",
            assignedUserIds: [],
            assignment: { type: 'individual', user_ids: [], department_ids: [], shared: true },
            selectedTrigger: null,
            triggerConfig: null,
            createTaskNow: true,
          }
          setFormData(defaultFormData)
          setInitialFormData({...defaultFormData}) // Create a copy for dirty checking
          setIsFormInitialized(true)
          setErrors({})
        }
      }, 300)
      return () => clearTimeout(timeout)
    }

    // Default to parent task for new tasks (when not editing)
    if (!isEditing && !initialData && open && !isFormInitialized) {
      const defaultFormData = {
        name: "",
        description: "",
        category: "General",
        priority: "5",
        due: "~today~",
        status: "To Do",
        deferrable: 0,
        missable: 0,
        isParent: true,
        parentId: null,
        parentName: null,
        workflowId: null,
        workflowName: null,
        taskType: "standard",
        taskTypeCategories: [],
        salesProbability: 1,
        checklist: [],
        link: "",
        linkTitle: "",
        assignedUserIds: [],
        assignment: { type: 'individual', user_ids: [], department_ids: [], shared: true },
        selectedTrigger: null,
        triggerConfig: null,
        createTaskNow: true,
      }
      setFormData(defaultFormData)
      setInitialFormData({...defaultFormData}) // Create a copy for dirty checking
      setIsFormInitialized(true)
    }
  }, [open, initialData, isEditing, parentTasks.length, isFormInitialized])

  // Check if form is dirty (has unsaved changes)
  const isDirty = () => {
    if (!initialFormData) return false
    
    // For editing mode, compare with initial data
    if (isEditing) {
      return JSON.stringify(formData) !== JSON.stringify(initialFormData)
    }
    
    // For new tasks, check if any field has been filled out (but only if initialFormData was set)
    // We need to check if any field differs from the initial empty state
    const hasChanges = 
      formData.name !== initialFormData.name ||
      formData.description !== initialFormData.description ||
      formData.category !== initialFormData.category ||
      formData.priority !== initialFormData.priority ||
      formData.due !== initialFormData.due ||
      formData.status !== initialFormData.status ||
      formData.deferrable !== initialFormData.deferrable ||
      formData.isParent !== initialFormData.isParent ||
      formData.parentId !== initialFormData.parentId ||
      formData.workflowId !== initialFormData.workflowId ||
      formData.taskType !== initialFormData.taskType ||
      JSON.stringify(formData.taskTypeCategories) !== JSON.stringify(initialFormData.taskTypeCategories) ||
      formData.salesProbability !== initialFormData.salesProbability ||
      JSON.stringify(formData.checklist) !== JSON.stringify(initialFormData.checklist) ||
      formData.link !== initialFormData.link ||
      formData.linkTitle !== initialFormData.linkTitle ||
      JSON.stringify(formData.assignedUserIds) !== JSON.stringify(initialFormData.assignedUserIds) ||
      formData.selectedTrigger !== initialFormData.selectedTrigger ||
      JSON.stringify(formData.triggerConfig) !== JSON.stringify(initialFormData.triggerConfig) ||
      formData.createTaskNow !== initialFormData.createTaskNow
    
    return hasChanges
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.name.trim()) {
      newErrors.name = "Task name is required"
    }

    if (!formData.description.trim()) {
      newErrors.description = "Description is required"
    }

    if (!formData.category) {
      newErrors.category = "Category is required"
    }

    if (!formData.priority) {
      newErrors.priority = "Priority is required"
    }

    if (!formData.due) {
      newErrors.due = "Due date is required"
    }

    // Allow "~today~" as a valid due date (Day of Task Creation)
    if (formData.due === "~today~") {
      // Remove error if it exists
      delete newErrors.due
    }

    if (!formData.status) {
      newErrors.status = "Status is required"
    }

    // Add validation for parent task in the validateForm function:
    if (!formData.isParent && !formData.parentId) {
      newErrors.parentId = "Parent task is required for subtasks"
    }

    // Remove validation for task type categories - they are now optional
    // if (formData.taskTypeCategories.length === 0) {
    //   newErrors.taskTypeCategories = "At least one task type category is required"
    // }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (validateForm()) {
      try {
        // Call the submit function
        await onSubmit(formData)
        
        // Close the modal immediately after successful submission
        onOpenChange(false)
        
        // Show success notification immediately
        if (!isEditing) {
          toast({
            title: "Task Created",
            description: `Task "${formData.name}" has been created successfully.`,
            variant: "success",
          })
        } else {
          toast({
            title: "Task Updated",
            description: `Task "${formData.name}" has been updated successfully.`,
            variant: "success",
          })
        }
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to save task. Please try again.",
          variant: "destructive",
        })
      }
    }
  }

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))

    // Clear error for this field when user makes a change
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  // Handle task type category selection
  const handleTaskTypeCategoryToggle = (categoryValue) => {
    const currentCategories = formData.taskTypeCategories || []
    const isSelected = currentCategories.includes(categoryValue)

    let newCategories
    if (isSelected) {
      newCategories = currentCategories.filter((cat) => cat !== categoryValue)
    } else {
      newCategories = [...currentCategories, categoryValue]
    }

    handleChange("taskTypeCategories", newCategories)
  }

  // Remove a task type category
  const removeTaskTypeCategory = (categoryValue) => {
    const currentCategories = formData.taskTypeCategories || []
    const newCategories = currentCategories.filter((cat) => cat !== categoryValue)
    handleChange("taskTypeCategories", newCategories)
  }

  // Handle user assignment toggle
  const handleUserAssignmentToggle = (userId) => {
    const currentUsers = formData.assignedUserIds || []
    const isSelected = currentUsers.includes(userId)

    let newUsers
    if (isSelected) {
      newUsers = currentUsers.filter((id) => id !== userId)
    } else {
      newUsers = [...currentUsers, userId]
    }

    handleChange("assignedUserIds", newUsers)
  }

  // Remove a user assignment
  const removeUserAssignment = (userId) => {
    const currentUsers = formData.assignedUserIds || []
    const newUsers = currentUsers.filter((id) => id !== userId)
    handleChange("assignedUserIds", newUsers)
  }

  // Task type categories options
  const taskTypeCategoryOptions = [
    { value: "quality", label: "Quality", color: "#10b981" },
    { value: "speed", label: "Speed", color: "#3b82f6" },
    { value: "procedural", label: "Procedural", color: "#8b5cf6" },
  ]

  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false)
  const [isDueDatePopoverOpen, setIsDueDatePopoverOpen] = useState(false)
  const [isTriggerDropdownOpen, setIsTriggerDropdownOpen] = useState(false)
  const [showTriggerConfig, setShowTriggerConfig] = useState(false)
  const [isWorkflowDropdownOpen, setIsWorkflowDropdownOpen] = useState(false)

  // Available trigger types
  const availableTriggers = [
    { value: "schedule", label: "Schedule", description: "Schedule-based trigger (Daily, Weekly, Monthly, etc.)" }
  ]

  // Time options for schedule
  const timeOptions = [
    "12:00 AM", "1:00 AM", "2:00 AM", "3:00 AM", "4:00 AM", "5:00 AM",
    "6:00 AM", "7:00 AM", "8:00 AM", "9:00 AM", "10:00 AM", "11:00 AM",
    "12:00 PM", "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM", "5:00 PM",
    "6:00 PM", "7:00 PM", "8:00 PM", "9:00 PM", "10:00 PM", "11:00 PM",
  ]

  // Day options
  const dayOptions = [
    { value: "Monday", label: "Mon" },
    { value: "Tuesday", label: "Tue" },
    { value: "Wednesday", label: "Wed" },
    { value: "Thursday", label: "Thu" },
    { value: "Friday", label: "Fri" },
    { value: "Saturday", label: "Sat" },
    { value: "Sunday", label: "Sun" },
  ]

  // Month day options
  const monthOptions = [
    "1st", "2nd", "3rd", "4th", "5th", "6th", "7th", "8th", "9th", "10th",
    "11th", "12th", "13th", "14th", "15th", "16th", "17th", "18th", "19th", "20th",
    "21st", "22nd", "23rd", "24th", "25th", "26th", "27th", "28th", "29th", "30th", "31st",
  ]

  // Initialize trigger config with defaults
  const getDefaultTriggerConfig = (triggerType) => {
    if (triggerType === "schedule") {
      return {
        triggerType: "schedule",
        scheduleType: "interval",
        timezone: "America/New_York",
        frequency: "day",
        minuteValue: "0",
        hourValue: "0",
        dayValue: ["Monday"],
        monthValue: "1st",
        quarterValue: "Q1",
        halfYearValue: "H1",
        annualMonthValue: "January",
        timeValue: "12:00 AM",
        startDate: new Date().toISOString().split("T")[0],
        startTime: new Date().toLocaleTimeString("en-US", {
          hour12: false,
          hour: "2-digit",
          minute: "2-digit",
        }),
        minuteInterval: 1,
        endType: "never",
        endDate: "",
        endOccurrences: 1,
      }
    }
    return {}
  }

  // Update trigger config helper
  const updateTriggerConfig = (key, value) => {
    const currentConfig = formData.triggerConfig || getDefaultTriggerConfig(formData.selectedTrigger)
    handleChange("triggerConfig", {
      ...currentConfig,
      [key]: value
    })
  }

  // Toggle day in dayValue array
  const toggleDay = (day) => {
    const currentConfig = formData.triggerConfig || getDefaultTriggerConfig("schedule")
    const currentDays = Array.isArray(currentConfig.dayValue) ? [...currentConfig.dayValue] : [currentConfig.dayValue || "Monday"]
    
    if (currentDays.includes(day)) {
      const newDays = currentDays.filter((d) => d !== day)
      if (newDays.length === 0) return // Don't allow removing all days
      updateTriggerConfig("dayValue", newDays)
    } else {
      updateTriggerConfig("dayValue", [...currentDays, day])
    }
  }

  // Add click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      const categoryDropdown = document.getElementById('task-type-categories-dropdown')
      const categoryTrigger = document.getElementById('task-type-categories-trigger')
      
      if (categoryDropdown && categoryTrigger && !categoryDropdown.contains(event.target) && !categoryTrigger.contains(event.target)) {
        setIsCategoryDropdownOpen(false)
      }
    }

    if (isCategoryDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isCategoryDropdownOpen])

  const sliderStyles = `
  input[type="range"] {
    -webkit-appearance: none;
    appearance: none;
  }
  
  input[type="range"]::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: hsl(var(--primary));
    cursor: pointer;
    border: 2px solid white;
    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
  }
  
  input[type="range"]::-moz-range-thumb {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: hsl(var(--primary));
    cursor: pointer;
    border: 2px solid white;
    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
  }
  
  input[type="range"]:disabled::-webkit-slider-thumb {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  input[type="range"]:disabled::-moz-range-thumb {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

  // Add this right after the opening of the Dialog component
  useEffect(() => {
    const styleElement = document.createElement("style")
    styleElement.innerHTML = sliderStyles
    document.head.appendChild(styleElement)

    return () => {
      document.head.removeChild(styleElement)
    }
  }, [])

  // Custom handler for modal open/close
  const handleOpenChange = (newOpen) => {
    if (!newOpen && isDirty()) {
      // Check if this is from clicking outside (no specific trigger) vs Close button
      // We'll use a ref to track if the close was triggered by the Close button
      const wasCloseButtonClicked = document.activeElement?.getAttribute('data-close-button') === 'true'
      
      if (wasCloseButtonClicked) {
        // Allow closing via Close button and show confirmation
        toast({
          title: "Unsaved Changes Discarded",
          description: "Your changes have been discarded.",
          variant: "default",
        });
        onOpenChange(newOpen);
      } else {
        // Prevent closing when clicking outside
        toast({
          title: "Unsaved Changes",
          description: "Please save or use the Close button to discard your changes.",
          variant: "destructive",
        });
        return;
      }
    } else {
      onOpenChange(newOpen);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="w-full max-w-none sm:max-w-[600px] lg:max-w-[800px] h-full sm:h-[600px] lg:h-[800px] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Task Details" : "Create New Task"}</DialogTitle>
          <DialogDescription>
            {isEditing ? "View the details of this task." : "Fill out the form below to create a new task."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6 py-4">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" className={errors.name ? "text-destructive" : ""}>
                Task Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                placeholder="Enter task name"
                className={errors.name ? "border-destructive" : ""}
                readOnly={isEditing}
              />
              {errors.name && <p className="text-sm text-destructive">{errors.name}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="description" className={errors.description ? "text-destructive" : ""}>
                Description <span className="text-destructive">*</span>
              </Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleChange("description", e.target.value)}
                placeholder="Enter task description"
                rows={3}
                className={errors.description ? "border-destructive" : ""}
                readOnly={isEditing}
              />
              {errors.description && <p className="text-sm text-destructive">{errors.description}</p>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="link">Link (Optional)</Label>
                <Input
                  id="link"
                  value={formData.link}
                  onChange={(e) => handleChange("link", e.target.value)}
                  placeholder="https://example.com"
                  className={errors.link ? "border-destructive" : ""}
                  readOnly={isEditing}
                />
                {errors.link && <p className="text-sm text-destructive">{errors.link}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="linkTitle">Link Title (Optional)</Label>
                <Input
                  id="linkTitle"
                  value={formData.linkTitle}
                  onChange={(e) => handleChange("linkTitle", e.target.value)}
                  placeholder="Link display name"
                  className={errors.linkTitle ? "border-destructive" : ""}
                  readOnly={isEditing}
                />
                {errors.linkTitle && <p className="text-sm text-destructive">{errors.linkTitle}</p>}
              </div>
            </div>

            {canAssignTasks && (
              <div className="space-y-2">
                <Label htmlFor="assignment">Assign To (Optional)</Label>
                <AssignmentSelector
                  value={formData.assignment}
                  onChange={(assignment) => {
                    handleChange("assignment", assignment)
                    // Also update assignedUserIds for backward compatibility
                    if (assignment.type === 'individual') {
                      handleChange("assignedUserIds", assignment.user_ids || [])
                    } else {
                      handleChange("assignedUserIds", [])
                    }
                  }}
                  users={users}
                  departments={departments}
                  workflowAssignment={null}
                />
                {formData.assignment && formData.assignment.type === 'individual' && formData.assignment.user_ids && formData.assignment.user_ids.length > 0 && (
                  <p className="text-sm text-muted-foreground">
                    Task will be assigned to: <span className="font-medium">
                      {formData.assignment.user_ids.map((userId, index) => {
                        const user = users.find(u => u.id.toString() === userId.toString())
                        const userName = user?.name || user?.email || `User ${userId}`
                        return (
                          <span key={userId}>
                            {userName}
                            {index < formData.assignment.user_ids.length - 1 ? ', ' : ''}
                          </span>
                        )
                      })}
                    </span>
                  </p>
                )}
                {formData.assignment && (formData.assignment.type === 'department' || formData.assignment.type === 'department_manager') && formData.assignment.department_ids && formData.assignment.department_ids.length > 0 && (
                  <p className="text-sm text-muted-foreground">
                    Task will be assigned to: <span className="font-medium">
                      {formData.assignment.type === 'department' ? 'all users in' : 'managers of'} {formData.assignment.department_ids.map((deptId, index) => {
                        const dept = departments.find(d => d.id === deptId)
                        const deptName = dept?.name || `Department ${deptId}`
                        return (
                          <span key={deptId}>
                            {deptName}
                            {index < formData.assignment.department_ids.length - 1 ? ', ' : ''}
                          </span>
                        )
                      })}
                    </span>
                  </p>
                )}
              </div>
            )}

            <div className="space-y-4 border-t pt-4">
              <div className="space-y-2">
                <Label>Task Type</Label>
                <div className="flex rounded-lg border p-1 bg-muted/50">
                  <button
                    type="button"
                    className={cn(
                      "flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                      formData.taskType === "standard"
                        ? "bg-background text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground",
                    )}
                    onClick={() => handleChange("taskType", "standard")}
                    disabled={isEditing}
                  >
                    <CheckSquare size={16} />
                    Standard
                  </button>
                  <button
                    type="button"
                    className={cn(
                      "flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                      formData.taskType === "sales"
                        ? "bg-background text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground",
                    )}
                    onClick={() => handleChange("taskType", "sales")}
                    disabled={isEditing}
                  >
                    <DollarSign size={16} />
                    Sales
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label className={errors.taskTypeCategories ? "text-destructive" : ""}>
                  Task Type Categories (Optional)
                </Label>
                <div className="relative">
                  <div
                    id="task-type-categories-trigger"
                    className={cn(
                      "w-full min-h-[40px] px-3 py-2 border rounded-md text-left bg-background flex items-center justify-between gap-2",
                      errors.taskTypeCategories && "border-destructive",
                      isEditing && "opacity-70 cursor-not-allowed",
                    )}
                    onClick={() => !isEditing && setIsCategoryDropdownOpen(!isCategoryDropdownOpen)}
                  >
                    <div className="flex items-center gap-2 flex-1 overflow-hidden">
                      {formData.taskTypeCategories && formData.taskTypeCategories.length > 0 ? (
                        <div className="flex flex-wrap items-center gap-1">
                          {formData.taskTypeCategories.map((categoryValue) => {
                            const category = taskTypeCategoryOptions.find((opt) => opt.value === categoryValue)
                            return (
                              <span
                                key={categoryValue}
                                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-white rounded-full"
                                style={{ backgroundColor: category?.color || "#6b7280" }}
                              >
                                {category?.label || categoryValue}
                                {!isEditing && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      removeTaskTypeCategory(categoryValue)
                                    }}
                                    className="hover:bg-black/20 rounded-full p-0.5"
                                  >
                                    <X size={10} />
                                  </button>
                                )}
                              </span>
                            )
                          })}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">Select task type categories...</span>
                      )}
                    </div>
                    <ChevronDown
                      size={16}
                      className={cn("transition-transform", isCategoryDropdownOpen && "rotate-180")}
                    />
                  </div>

                  {isCategoryDropdownOpen && !isEditing && (
                    <div id="task-type-categories-dropdown" className="absolute top-full left-0 right-0 mt-1 bg-background border rounded-md shadow-lg z-50">
                      <div className="p-1">
                        {taskTypeCategoryOptions.map((option) => {
                          const isSelected = formData.taskTypeCategories?.includes(option.value) || false
                          return (
                            <button
                              key={option.value}
                              type="button"
                              className={cn(
                                "w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md transition-colors",
                                isSelected ? "bg-muted text-foreground" : "hover:bg-muted/50",
                              )}
                              onClick={() => handleTaskTypeCategoryToggle(option.value)}
                            >
                              <div
                                className="w-3 h-3 rounded-sm flex-shrink-0"
                                style={{ backgroundColor: option.color }}
                              />
                              <span className="flex-1 text-left">{option.label}</span>
                              {isSelected && <CheckSquare size={16} style={{ color: option.color }} />}
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </div>
                {errors.taskTypeCategories && <p className="text-sm text-destructive">{errors.taskTypeCategories}</p>}
              </div>

              {formData.taskType === "sales" && (
                <div className="flex items-center space-x-2">
                  <Switch
                    id="showProbabilitySlider"
                    checked={!!formData.salesProbability}
                    onCheckedChange={(checked) => handleChange("salesProbability", checked ? 1 : 0)}
                    disabled={isEditing}
                  />
                  <Label htmlFor="showProbabilitySlider">Show probability of closing sale slider</Label>
                </div>
              )}

              <div className="space-y-2">
                <Label>Checklist Items</Label>
                <p className="text-sm text-muted-foreground">
                  Add checklist items to this task. Tasks can be completed regardless of checklist status.
                </p>
                <Checklist
                  items={formData.checklist}
                  onUpdate={(updatedChecklist) => handleChange("checklist", updatedChecklist)}
                  isCollapsible={false}
                  showProgress={false}
                  maxHeight="200px"
                  isCheckable={false}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="category" className={errors.category ? "text-destructive" : ""}>
                  Category <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => handleChange("category", value)}
                  disabled={isEditing}
                >
                  <SelectTrigger id="category" className={errors.category ? "border-destructive" : ""}>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((category) => (
                      <SelectItem key={category.id} value={category.name}>
                        {category.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.category && <p className="text-sm text-destructive">{errors.category}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="priority" className={errors.priority ? "text-destructive" : ""}>
                  Priority <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={formData.priority}
                  onValueChange={(value) => handleChange("priority", value)}
                  disabled={isEditing}
                >
                  <SelectTrigger id="priority" className={errors.priority ? "border-destructive" : ""}>
                    <SelectValue placeholder="Select priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 (Lowest)</SelectItem>
                    <SelectItem value="2">2</SelectItem>
                    <SelectItem value="3">3</SelectItem>
                    <SelectItem value="4">4</SelectItem>
                    <SelectItem value="5">5</SelectItem>
                    <SelectItem value="6">6</SelectItem>
                    <SelectItem value="7">7</SelectItem>
                    <SelectItem value="8">8</SelectItem>
                    <SelectItem value="9">9</SelectItem>
                    <SelectItem value="10">10 (Highest)</SelectItem>
                  </SelectContent>
                </Select>
                {errors.priority && <p className="text-sm text-destructive">{errors.priority}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="status" className={errors.status ? "text-destructive" : ""}>
                  Status <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => handleChange("status", value)}
                  disabled={isEditing}
                >
                  <SelectTrigger id="status" className={errors.status ? "border-destructive" : ""}>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="To Do">To Do</SelectItem>
                    <SelectItem value="In Progress">In Progress</SelectItem>
                    <SelectItem value="Completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
                {errors.status && <p className="text-sm text-destructive">{errors.status}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="due-date" className={errors.due ? "text-destructive" : ""}>
                  Due Date <span className="text-destructive">*</span>
                </Label>
                <Popover open={isDueDatePopoverOpen} onOpenChange={setIsDueDatePopoverOpen}>
                  <PopoverTrigger asChild disabled={isEditing}>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !formData.due && "text-muted-foreground",
                        errors.due && "border-destructive",
                        isEditing && "opacity-70 cursor-not-allowed",
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.due === "~today~" ? (
                        <span>Day of Task Creation (Today)</span>
                      ) : formData.due ? (
                        format(new Date(formData.due + 'T12:00:00'), "PPP")
                      ) : (
                        <span>Pick a date</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={formData.due && formData.due !== "~today~" ? new Date(formData.due + 'T12:00:00') : undefined}
                      onSelect={(date) => {
                        if (date) {
                          // Extract date components directly to avoid timezone issues
                          const year = date.getFullYear();
                          const month = String(date.getMonth() + 1).padStart(2, '0');
                          const day = String(date.getDate()).padStart(2, '0');
                          const dateString = `${year}-${month}-${day}`;
                          handleChange("due", dateString);
                          // Close the popover after selecting a date
                          setIsDueDatePopoverOpen(false);
                        } else {
                          handleChange("due", "");
                        }
                      }}
                      disabled={(date) => {
                        // Disable past dates and today
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        return date < today;
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                {formData.due === "~today~" && (
                  <p className="text-sm text-muted-foreground">
                    Due date will be set to the day the task is created.
                  </p>
                )}
                {errors.due && <p className="text-sm text-destructive">{errors.due}</p>}
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="deferrable"
                checked={!!formData.deferrable}
                onCheckedChange={(checked) => handleChange("deferrable", checked ? 1 : 0)}
                disabled={isEditing}
              />
              <Label htmlFor="deferrable">This task can be deferred if needed</Label>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="missable"
                checked={!!formData.missable}
                onCheckedChange={(checked) => handleChange("missable", checked ? 1 : 0)}
                disabled={isEditing}
              />
              <Label htmlFor="missable">This task can be missed if overdue</Label>
            </div>
            {/* Add this new section after the deferrable switch in the form: */}

            <div className="space-y-4 border-t pt-4 mt-4">
              {parentTasks.length > 0 ? (
                <>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="isParent"
                      checked={formData.isParent}
                      onCheckedChange={(checked) => {
                        handleChange("isParent", checked)
                        if (checked) {
                          handleChange("parentId", null)
                          handleChange("parentName", null)
                        }
                      }}
                      disabled={isEditing}
                    />
                    <Label htmlFor="isParent">This is a parent task</Label>
                  </div>

                  {!formData.isParent && (
                    <div className="space-y-2">
                      <Label htmlFor="parentTask">Parent Task *</Label>
                      <Select
                        value={formData.parentId ? formData.parentId.toString() : ""}
                        onValueChange={(value) => {
                          const parent = parentTasks.find((task) => task.id.toString() === value)
                          handleChange("parentId", value)
                          handleChange("parentName", parent?.name || null)
                        }}
                        disabled={isEditing || formData.isParent}
                      >
                        <SelectTrigger id="parentTask" className={`text-foreground ${errors.parentId ? "border-destructive" : ""}`}>
                          <SelectValue placeholder="Select parent task" />
                        </SelectTrigger>
                        <SelectContent className="text-foreground">
                          {parentTasks.map((task) => (
                            <SelectItem key={task.id} value={task.id.toString()} className="text-foreground">
                              {task.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.parentId && <p className="text-sm text-destructive">{errors.parentId}</p>}
                    </div>
                  )}
                </>
              ) : (
                <div className="flex items-center space-x-2">
                  <Switch id="isParent" checked={true} disabled={true} />
                  <Label htmlFor="isParent" className="flex items-center">
                    This is a parent task
                    <span className="ml-2 text-xs text-muted-foreground">(First task must be a parent task)</span>
                  </Label>
                </div>
              )}
            </div>
            {/* Add a new workflow selection field to the form after the category field. */}
            <div className="space-y-2">
              <Label htmlFor="workflow">Link to Workflow</Label>
              <Popover open={isWorkflowDropdownOpen} onOpenChange={setIsWorkflowDropdownOpen} modal={true}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={isWorkflowDropdownOpen}
                    className="w-full justify-between"
                    disabled={isEditing}
                  >
                    {formData.workflowId
                      ? availableWorkflows.find((w) => w.id === formData.workflowId)?.name || "Select a workflow"
                      : "Select a workflow (optional)"}
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0 max-h-[300px] overflow-y-auto" align="start" side="top">
                  <Command>
                    <CommandInput placeholder="Search workflows..." />
                    <CommandList>
                      <CommandEmpty>No workflows found.</CommandEmpty>
                      <CommandGroup>
                        <CommandItem
                          value="none"
                          onSelect={() => {
                            handleChange("workflowId", null)
                            handleChange("workflowName", null)
                            setIsWorkflowDropdownOpen(false)
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              !formData.workflowId ? "opacity-100" : "opacity-0"
                            )}
                          />
                          No workflow
                        </CommandItem>
                        {availableWorkflows.map((workflow) => (
                          <CommandItem
                            key={workflow.id}
                            value={workflow.name}
                            onSelect={() => {
                              handleChange("workflowId", workflow.id)
                              handleChange("workflowName", workflow.name)
                              // Clear trigger fields when workflow is selected
                              handleChange("selectedTrigger", null)
                              handleChange("triggerConfig", null)
                              setShowTriggerConfig(false)
                              setIsWorkflowDropdownOpen(false)
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                formData.workflowId === workflow.id ? "opacity-100" : "opacity-0"
                              )}
                            />
                            <div className="flex items-center gap-2 flex-1">
                              <span className="flex-1">{workflow.name}</span>
                              {(workflow.status === 'inactive' || workflow.status === 'draft') && (
                                <Badge variant="secondary" className="text-xs h-4 px-1.5 shrink-0 bg-red-500 text-white">
                                  Inactive
                                </Badge>
                              )}
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {formData.workflowId && (
                <p className="text-sm text-muted-foreground">
                  This task will be linked to the workflow: <span className="font-medium">{formData.workflowName}</span>
                </p>
              )}
            </div>

            {/* Schedule with Workflow Trigger Section */}
            {!isEditing && !formData.workflowId && (
              <div className="space-y-4 border-t pt-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="trigger">Schedule with Workflow Trigger (Optional)</Label>
                  <p className="text-sm text-muted-foreground">
                    Select a trigger to automatically create this task on a schedule or via integration.
                  </p>
                  <Select
                    value={formData.selectedTrigger || ""}
                    onValueChange={(value) => {
                      if (value === "none") {
                        handleChange("selectedTrigger", null)
                        handleChange("triggerConfig", null)
                        setShowTriggerConfig(false)
                      } else {
                        handleChange("selectedTrigger", value)
                        // Set default config based on trigger type
                        const defaultConfig = getDefaultTriggerConfig(value)
                        handleChange("triggerConfig", defaultConfig)
                        // Auto-expand config for schedule triggers
                        setShowTriggerConfig(value === "schedule")
                        // Set due date to "Day of Task Creation" when trigger is selected
                        handleChange("due", "~today~")
                      }
                    }}
                    disabled={isEditing}
                  >
                    <SelectTrigger id="trigger" className="w-full">
                      <SelectValue placeholder="Select a trigger (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No trigger</SelectItem>
                      {availableTriggers.map((trigger) => (
                        <SelectItem key={trigger.value} value={trigger.value}>
                          {trigger.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {formData.selectedTrigger && (
                  <>
                    <div className="space-y-2">
                      <Label>Task Creation Timing</Label>
                      <div className="flex rounded-lg border p-1 bg-muted/50">
                        <button
                          type="button"
                          className={cn(
                            "flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                            formData.createTaskNow
                              ? "bg-background text-foreground shadow-sm"
                              : "text-muted-foreground hover:text-foreground",
                          )}
                          onClick={() => handleChange("createTaskNow", true)}
                          disabled={isEditing}
                        >
                          Create task now
                        </button>
                        <button
                          type="button"
                          className={cn(
                            "flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                            !formData.createTaskNow
                              ? "bg-background text-foreground shadow-sm"
                              : "text-muted-foreground hover:text-foreground",
                          )}
                          onClick={() => handleChange("createTaskNow", false)}
                          disabled={isEditing}
                        >
                          Create on next trigger
                        </button>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {formData.createTaskNow 
                          ? "The task will be created immediately, and future instances will be created automatically when the trigger fires."
                          : "The task will only be created when the trigger fires next. No task will be created now."}
                      </p>
                    </div>
                    {formData.selectedTrigger === "schedule" && (
                      <div className="space-y-4 p-4 bg-muted/30 rounded-md border">
                        <div className="flex items-center justify-between">
                          <Label className="text-sm font-medium">Schedule Configuration</Label>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowTriggerConfig(!showTriggerConfig)}
                            className="h-8"
                          >
                            {showTriggerConfig ? "Hide" : "Configure"}
                            <ChevronDown
                              size={16}
                              className={cn("ml-2 transition-transform", showTriggerConfig && "rotate-180")}
                            />
                          </Button>
                        </div>
                        
                        {showTriggerConfig && (
                          <div className="space-y-4 pt-2">
                            {/* Timezone */}
                            <div className="space-y-2">
                              <Label>Timezone</Label>
                              <Select
                                value={formData.triggerConfig?.timezone || "UTC"}
                                onValueChange={(value) => updateTriggerConfig("timezone", value)}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="America/New_York">Eastern Time (ET)</SelectItem>
                                  <SelectItem value="America/Chicago">Central Time (CT)</SelectItem>
                                  <SelectItem value="America/Denver">Mountain Time (MT)</SelectItem>
                                  <SelectItem value="America/Los_Angeles">Pacific Time (PT)</SelectItem>
                                  <SelectItem value="UTC">UTC</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>

                            {/* Start Date & Time */}
                            <div className="grid grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <Label>Start Date</Label>
                                <Input
                                  type="date"
                                  className="cursor-pointer disabled:cursor-not-allowed"
                                  value={formData.triggerConfig?.startDate || new Date().toISOString().split("T")[0]}
                                  onChange={(e) => updateTriggerConfig("startDate", e.target.value)}
                                />
                              </div>
                              <div className="space-y-2">
                                <Label>Start Time</Label>
                                <Input
                                  type="time"
                                  value={formData.triggerConfig?.startTime || new Date().toLocaleTimeString("en-US", {
                                    hour12: false,
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })}
                                  onChange={(e) => updateTriggerConfig("startTime", e.target.value)}
                                />
                              </div>
                            </div>

                            {/* Frequency */}
                            <div className="space-y-2">
                              <Label>Run every</Label>
                              <div className="flex items-center gap-2">
                                <Select
                                  value={formData.triggerConfig?.frequency || "day"}
                                  onValueChange={(value) => updateTriggerConfig("frequency", value)}
                                >
                                  <SelectTrigger className="flex-1">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="minute">minute</SelectItem>
                                    <SelectItem value="hour">hour</SelectItem>
                                    <SelectItem value="day">day</SelectItem>
                                    <SelectItem value="week">week</SelectItem>
                                    <SelectItem value="month">month</SelectItem>
                                    <SelectItem value="quarter">quarter</SelectItem>
                                    <SelectItem value="biannual">biannual</SelectItem>
                                    <SelectItem value="annual">annual</SelectItem>
                                  </SelectContent>
                                </Select>
                                
                                {/* Minute interval for minute frequency */}
                                {formData.triggerConfig?.frequency === "minute" && (
                                  <div className="flex items-center gap-2">
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      onClick={() => {
                                        const current = formData.triggerConfig?.minuteInterval || 1
                                        const options = [1, 5, 10, 15, 30, 45]
                                        const currentIndex = options.indexOf(current)
                                        const newIndex = currentIndex <= 0 ? options.length - 1 : currentIndex - 1
                                        updateTriggerConfig("minuteInterval", options[newIndex])
                                      }}
                                    >
                                      ▼
                                    </Button>
                                    <div className="w-12 text-center font-medium">
                                      {formData.triggerConfig?.minuteInterval || 1}
                                    </div>
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      onClick={() => {
                                        const current = formData.triggerConfig?.minuteInterval || 1
                                        const options = [1, 5, 10, 15, 30, 45]
                                        const currentIndex = options.indexOf(current)
                                        const newIndex = currentIndex >= options.length - 1 ? 0 : currentIndex + 1
                                        updateTriggerConfig("minuteInterval", options[newIndex])
                                      }}
                                    >
                                      ▲
                                    </Button>
                                    <span className="text-sm text-muted-foreground">
                                      minute{(formData.triggerConfig?.minuteInterval || 1) !== 1 ? 's' : ''}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Hour frequency - minutes past the hour */}
                            {formData.triggerConfig?.frequency === "hour" && (
                              <div className="space-y-2">
                                <Label>Minutes past the hour</Label>
                                <Select
                                  value={`at ${formData.triggerConfig?.minuteValue || "0"} minutes past the hour`}
                                  onValueChange={(value) => {
                                    const match = value.match(/at (\d+) minutes/)
                                    if (match) updateTriggerConfig("minuteValue", match[1])
                                  }}
                                >
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {Array.from({ length: 60 }, (_, i) => (
                                      <SelectItem key={i} value={`at ${i} minutes past the hour`}>
                                        at {i} minutes past the hour
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}

                            {/* Day frequency - time */}
                            {formData.triggerConfig?.frequency === "day" && (
                              <div className="space-y-2">
                                <Label>Time</Label>
                                <Select
                                  value={formData.triggerConfig?.timeValue || "12:00 AM"}
                                  onValueChange={(value) => updateTriggerConfig("timeValue", value)}
                                >
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {timeOptions.map((option) => (
                                      <SelectItem key={option} value={option}>
                                        at {option}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}

                            {/* Week frequency - days and time */}
                            {formData.triggerConfig?.frequency === "week" && (
                              <>
                                <div className="space-y-2">
                                  <Label>On</Label>
                                  <div className="flex flex-wrap gap-2">
                                    {dayOptions.map((day) => {
                                      const currentDays = Array.isArray(formData.triggerConfig?.dayValue)
                                        ? formData.triggerConfig.dayValue
                                        : [formData.triggerConfig?.dayValue || "Monday"]
                                      const isSelected = currentDays.includes(day.value)
                                      return (
                                        <button
                                          key={day.value}
                                          type="button"
                                          onClick={() => toggleDay(day.value)}
                                          className={cn(
                                            "px-3 py-1.5 text-sm rounded-md border transition-colors",
                                            isSelected
                                              ? "bg-primary text-primary-foreground border-primary"
                                              : "bg-background hover:bg-muted"
                                          )}
                                        >
                                          {day.label}
                                        </button>
                                      )
                                    })}
                                  </div>
                                </div>
                                <div className="space-y-2">
                                  <Label>At</Label>
                                  <Select
                                    value={formData.triggerConfig?.timeValue || "12:00 AM"}
                                    onValueChange={(value) => updateTriggerConfig("timeValue", value)}
                                  >
                                    <SelectTrigger>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {timeOptions.map((option) => (
                                        <SelectItem key={option} value={option}>
                                          at {option}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              </>
                            )}

                            {/* Month frequency - day of month and time */}
                            {formData.triggerConfig?.frequency === "month" && (
                              <>
                                <div className="space-y-2">
                                  <Label>Day of month</Label>
                                  <Select
                                    value={formData.triggerConfig?.monthValue || "1st"}
                                    onValueChange={(value) => updateTriggerConfig("monthValue", value)}
                                  >
                                    <SelectTrigger>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {monthOptions.map((option) => (
                                        <SelectItem key={option} value={option}>
                                          on the {option}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="space-y-2">
                                  <Label>Time</Label>
                                  <Select
                                    value={formData.triggerConfig?.timeValue || "12:00 AM"}
                                    onValueChange={(value) => updateTriggerConfig("timeValue", value)}
                                  >
                                    <SelectTrigger>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {timeOptions.map((option) => (
                                        <SelectItem key={option} value={option}>
                                          at {option}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              </>
                            )}

                            {/* Quarter frequency */}
                            {formData.triggerConfig?.frequency === "quarter" && (
                              <>
                                <div className="space-y-2">
                                  <Label>Quarter start</Label>
                                  <Select
                                    value={formData.triggerConfig?.quarterValue || "Q1"}
                                    onValueChange={(value) => updateTriggerConfig("quarterValue", value)}
                                  >
                                    <SelectTrigger>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="Q1">Starting January (Jan, Apr, Jul, Oct)</SelectItem>
                                      <SelectItem value="Q2">Starting February (Feb, May, Aug, Nov)</SelectItem>
                                      <SelectItem value="Q3">Starting March (Mar, Jun, Sep, Dec)</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="space-y-2">
                                  <Label>Day of month</Label>
                                  <Select
                                    value={formData.triggerConfig?.monthValue || "1st"}
                                    onValueChange={(value) => updateTriggerConfig("monthValue", value)}
                                  >
                                    <SelectTrigger>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {monthOptions.map((option) => (
                                        <SelectItem key={option} value={option}>
                                          on the {option}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="space-y-2">
                                  <Label>Time</Label>
                                  <Select
                                    value={formData.triggerConfig?.timeValue || "12:00 AM"}
                                    onValueChange={(value) => updateTriggerConfig("timeValue", value)}
                                  >
                                    <SelectTrigger>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {timeOptions.map((option) => (
                                        <SelectItem key={option} value={option}>
                                          at {option}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              </>
                            )}

                            {/* Biannual frequency */}
                            {formData.triggerConfig?.frequency === "biannual" && (
                              <>
                                <div className="space-y-2">
                                  <Label>Half-year period</Label>
                                  <Select
                                    value={formData.triggerConfig?.halfYearValue || "H1"}
                                    onValueChange={(value) => updateTriggerConfig("halfYearValue", value)}
                                  >
                                    <SelectTrigger>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="H1">January & July</SelectItem>
                                      <SelectItem value="H2">February & August</SelectItem>
                                      <SelectItem value="H3">March & September</SelectItem>
                                      <SelectItem value="H4">April & October</SelectItem>
                                      <SelectItem value="H5">May & November</SelectItem>
                                      <SelectItem value="H6">June & December</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="space-y-2">
                                  <Label>Day of month</Label>
                                  <Select
                                    value={formData.triggerConfig?.monthValue || "1st"}
                                    onValueChange={(value) => updateTriggerConfig("monthValue", value)}
                                  >
                                    <SelectTrigger>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {monthOptions.map((option) => (
                                        <SelectItem key={option} value={option}>
                                          on the {option}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="space-y-2">
                                  <Label>Time</Label>
                                  <Select
                                    value={formData.triggerConfig?.timeValue || "12:00 AM"}
                                    onValueChange={(value) => updateTriggerConfig("timeValue", value)}
                                  >
                                    <SelectTrigger>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {timeOptions.map((option) => (
                                        <SelectItem key={option} value={option}>
                                          at {option}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              </>
                            )}

                            {/* Annual frequency */}
                            {formData.triggerConfig?.frequency === "annual" && (
                              <>
                                <div className="space-y-2">
                                  <Label>Month</Label>
                                  <Select
                                    value={formData.triggerConfig?.annualMonthValue || "January"}
                                    onValueChange={(value) => updateTriggerConfig("annualMonthValue", value)}
                                  >
                                    <SelectTrigger>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"].map((month) => (
                                        <SelectItem key={month} value={month}>
                                          {month}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="space-y-2">
                                  <Label>Day of month</Label>
                                  <Select
                                    value={formData.triggerConfig?.monthValue || "1st"}
                                    onValueChange={(value) => updateTriggerConfig("monthValue", value)}
                                  >
                                    <SelectTrigger>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {monthOptions.map((option) => (
                                        <SelectItem key={option} value={option}>
                                          on the {option}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="space-y-2">
                                  <Label>Time</Label>
                                  <Select
                                    value={formData.triggerConfig?.timeValue || "12:00 AM"}
                                    onValueChange={(value) => updateTriggerConfig("timeValue", value)}
                                  >
                                    <SelectTrigger>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {timeOptions.map((option) => (
                                        <SelectItem key={option} value={option}>
                                          at {option}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              </>
                            )}

                            {/* Schedule Summary */}
                            {formData.triggerConfig?.frequency && (
                              <div className="p-3 bg-muted rounded-md">
                                <p className="text-sm font-medium mb-1">Schedule Summary</p>
                                <p className="text-xs text-muted-foreground">
                                  {formData.triggerConfig.frequency === "minute" && 
                                    `Runs every ${formData.triggerConfig.minuteInterval || 1} minute${(formData.triggerConfig.minuteInterval || 1) !== 1 ? 's' : ''}`}
                                  {formData.triggerConfig.frequency === "hour" && 
                                    `Runs at ${formData.triggerConfig.minuteValue || "0"} minutes past every hour`}
                                  {formData.triggerConfig.frequency === "day" && 
                                    `Runs daily at ${formData.triggerConfig.timeValue || "12:00 AM"}`}
                                  {formData.triggerConfig.frequency === "week" && 
                                    `Runs weekly at ${formData.triggerConfig.timeValue || "12:00 AM"} on ${Array.isArray(formData.triggerConfig.dayValue) ? formData.triggerConfig.dayValue.join(", ") : formData.triggerConfig.dayValue || "Monday"}`}
                                  {formData.triggerConfig.frequency === "month" && 
                                    `Runs monthly at ${formData.triggerConfig.timeValue || "12:00 AM"} on the ${formData.triggerConfig.monthValue || "1st"}`}
                                  {formData.triggerConfig.frequency === "quarter" && 
                                    `Runs quarterly at ${formData.triggerConfig.timeValue || "12:00 AM"} on the ${formData.triggerConfig.monthValue || "1st"}`}
                                  {formData.triggerConfig.frequency === "biannual" && 
                                    `Runs biannually at ${formData.triggerConfig.timeValue || "12:00 AM"} on the ${formData.triggerConfig.monthValue || "1st"}`}
                                  {formData.triggerConfig.frequency === "annual" && 
                                    `Runs annually at ${formData.triggerConfig.timeValue || "12:00 AM"} on ${formData.triggerConfig.monthValue || "1st"} of ${formData.triggerConfig.annualMonthValue || "January"}`}
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button 
              type="button" 
              variant="outline" 
              onClick={(e) => {
                e.currentTarget.setAttribute('data-close-button', 'true');
                handleOpenChange(false);
              }}
            >
              Close
            </Button>
            {!isEditing && (
              <Button type="submit" disabled={isSubmitting} className="dark:text-white">
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Create Task
              </Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
